
import { create } from 'zustand';
import { db, type DBWorkspace, type DBFile } from '@/lib/storage/db';
import { ensurePersistentStorage } from '@/lib/storage/persistence';

// ---------- Types ----------

/** Workspace metadata (no file content) */
export interface WorkspaceMeta {
  id: string;
  title: string;
  subtitle?: string;
  createdAt: Date;
  updatedAt: Date;
  fileCount: number;
  totalSize: number;
}

/** File metadata (no content) */
export interface FileMeta {
  id: string;
  filename: string;
  displayName: string;
  order: number;
  size: number;
}

interface WorkspaceState {
  // All workspaces (metadata only)
  workspaces: WorkspaceMeta[];
  activeWorkspaceId: string | null;

  // Files for active workspace (metadata only)
  files: FileMeta[];
  activeFileId: string | null;

  // Content of the currently viewed file (loaded on demand)
  activeFileContent: string | null;
  isContentLoading: boolean;

  isLoaded: boolean;

  // Actions
  initialize: () => Promise<void>;
  createWorkspace: (title: string, inputFiles: { filename: string; content: string }[]) => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  deleteWorkspace: (workspaceId: string) => Promise<void>;
  renameWorkspace: (workspaceId: string, title: string) => Promise<void>;
  /** Rename a file in the active workspace. Updates filename (keeping the
      extension if the user omits one) and displayName together. */
  renameFile: (fileId: string, newName: string) => Promise<void>;
  setActiveFile: (fileId: string) => Promise<void>;
  /** Update the active file's content in place + persist (no scroll/view
      transition) — used by inline edits like task-list checkbox toggles. */
  updateActiveFileContent: (content: string) => Promise<void>;
  addFiles: (files: { filename: string; content: string }[]) => Promise<void>;
  removeFile: (fileId: string) => Promise<void>;
  reorderFiles: (fromIndex: number, toIndex: number) => void;
  reorderWorkspaces: (fromIndex: number, toIndex: number) => void;
  /**
   * Move a file out of its current workspace into `targetWorkspaceId`.
   * Drops it at the end of the target's file list. If the moved file was
   * the active file in the source workspace, falls back to the next file
   * there. Returns `true` if the move succeeded.
   */
  moveFileToWorkspace: (fileId: string, targetWorkspaceId: string) => Promise<boolean>;
  /**
   * Promote a single file into its own brand-new workspace. The source
   * workspace loses the file; the new workspace gets it as its only file
   * and becomes active.
   */
  promoteFileToNewWorkspace: (fileId: string, newTitle?: string) => Promise<void>;
}

// Monotonic tokens guarding the async switch paths: rapid A→B→A switching
// must not let a slow B read resolve last and clobber state ("last write
// wins" instead of "last USER CHOICE wins"). Only the newest request commits.
let workspaceSwitchToken = 0;
let fileSwitchToken = 0;

function generateId(): string {
  return crypto.randomUUID();
}

/**
 * The marketing routes seed their own workspaces — "portfolio: <slug>" on
 * /p/:slug and "the chronicle" on /projects. Those are re-fetchable
 * content, not the user's documents, so they must not count as "the user
 * has something worth protecting".
 */
function isSystemWorkspaceTitle(title: string): boolean {
  return title.startsWith('portfolio: ') || title === 'the chronicle';
}

function deriveDisplayName(filename: string): string {
  return filename
    .replace(/\.md$/i, '')
    .replace(/[_-]/g, ' ')
    .replace(/^\d+\s*/, '')
    .trim() || filename;
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  workspaces: [],
  activeWorkspaceId: null,
  files: [],
  activeFileId: null,
  activeFileContent: null,
  isContentLoading: false,
  isLoaded: false,

  // ---------- Initialize ----------
  initialize: async () => {
    try {
      // Load all workspace metadata (no files)
      const dbWorkspaces = await db.workspaces.orderBy('updatedAt').reverse().toArray();
      const workspaces: WorkspaceMeta[] = dbWorkspaces.map((ws) => ({
        id: ws.id,
        title: ws.title,
        subtitle: ws.subtitle,
        createdAt: ws.createdAt,
        updatedAt: ws.updatedAt,
        fileCount: ws.fileCount || 0,
        totalSize: ws.totalSize || 0,
      }));

      if (workspaces.length === 0) {
        set({ workspaces: [], isLoaded: true });
        return;
      }

      // Auto-select the most recent workspace — skipping the marketing
      // routes' system workspaces ("portfolio: <slug>" / "the chronicle"),
      // so browsing /projects or /p/:slug never steals the editor's
      // default workspace from the user's real notes.
      const activeWs = workspaces.find((w) => !isSystemWorkspaceTitle(w.title)) ?? workspaces[0];
      const dbFiles = await db.files
        .where('workspaceId')
        .equals(activeWs.id)
        .sortBy('order');

      const files: FileMeta[] = dbFiles.map((f) => ({
        id: f.id,
        filename: f.filename,
        displayName: f.displayName,
        order: f.order,
        size: f.size,
      }));

      // Load content of first file
      let activeFileContent: string | null = null;
      if (files.length > 0) {
        const firstFile = await db.files.get(files[0].id);
        activeFileContent = firstFile?.content ?? null;
      }

      set({
        workspaces,
        activeWorkspaceId: activeWs.id,
        files,
        activeFileId: files.length > 0 ? files[0].id : null,
        activeFileContent,
        isLoaded: true,
      });
    } catch (e) {
      console.error('Failed to initialize workspace store:', e);
      set({ isLoaded: true });
    }
  },

  // ---------- Create Workspace ----------
  createWorkspace: async (title, inputFiles) => {
    const id = generateId();
    const now = new Date();
    const sorted = [...inputFiles].sort((a, b) => a.filename.localeCompare(b.filename));

    const files: (FileMeta & { content: string })[] = sorted.map((f, i) => ({
      id: generateId(),
      filename: f.filename,
      displayName: deriveDisplayName(f.filename),
      content: f.content,
      order: i,
      size: new Blob([f.content]).size,
    }));

    const totalSize = files.reduce((sum, f) => sum + f.size, 0);

    // Persist to IndexedDB
    const dbWorkspace: DBWorkspace = {
      id,
      title,
      createdAt: now,
      updatedAt: now,
      theme: 'system',
      fileCount: files.length,
      totalSize,
    };

    const dbFiles: DBFile[] = files.map((f) => ({
      id: f.id,
      workspaceId: id,
      filename: f.filename,
      displayName: f.displayName,
      content: f.content,
      order: f.order,
      size: f.size,
    }));

    await db.transaction('rw', db.workspaces, db.files, async () => {
      await db.workspaces.add(dbWorkspace);
      await db.files.bulkAdd(dbFiles);
    });

    // File metadata (no content in state)
    const fileMetas: FileMeta[] = files.map(({ content: _, ...meta }) => meta);

    // Load first file's content
    const firstContent = files.length > 0 ? files[0].content : null;

    const newWsMeta: WorkspaceMeta = {
      id,
      title,
      createdAt: now,
      updatedAt: now,
      fileCount: files.length,
      totalSize,
    };

    set((state) => ({
      workspaces: [newWsMeta, ...state.workspaces],
      activeWorkspaceId: id,
      files: fileMetas,
      activeFileId: fileMetas.length > 0 ? fileMetas[0].id : null,
      activeFileContent: firstContent,
    }));

    // The user's first documents just landed in IndexedDB — ask the browser
    // to stop treating them as evictable. Skipped for the marketing routes'
    // own workspaces: a visitor reading /p/:slug has nothing to protect yet.
    if (!isSystemWorkspaceTitle(title)) void ensurePersistentStorage();
  },

  // ---------- Switch Workspace ----------
  switchWorkspace: async (workspaceId) => {
    const { activeWorkspaceId } = get();
    if (workspaceId === activeWorkspaceId) return;

    const token = ++workspaceSwitchToken;
    set({ isContentLoading: true });

    const dbFiles = await db.files
      .where('workspaceId')
      .equals(workspaceId)
      .sortBy('order');
    if (token !== workspaceSwitchToken) return; // superseded by a newer switch

    const files: FileMeta[] = dbFiles.map((f) => ({
      id: f.id,
      filename: f.filename,
      displayName: f.displayName,
      order: f.order,
      size: f.size,
    }));

    let activeFileContent: string | null = null;
    if (files.length > 0) {
      const firstFile = await db.files.get(files[0].id);
      activeFileContent = firstFile?.content ?? null;
    }
    if (token !== workspaceSwitchToken) return; // superseded by a newer switch

    set({
      activeWorkspaceId: workspaceId,
      files,
      activeFileId: files.length > 0 ? files[0].id : null,
      activeFileContent,
      isContentLoading: false,
    });

    // If this workspace has a saved atmosphere, switch the ambient layer
    // to match. Import lazily to avoid a circular dep at module load.
    try {
      const dbWs = await db.workspaces.get(workspaceId);
      if (dbWs?.atmosphere) {
        const { useThemeStore } = await import('@/stores/theme-store');
        useThemeStore.getState().setAtmosphere(dbWs.atmosphere);
      }
    } catch {
      /* ignore — atmosphere persistence is best-effort */
    }
  },

  // ---------- Delete Workspace ----------
  deleteWorkspace: async (workspaceId) => {
    await db.transaction('rw', [db.workspaces, db.files, db.embeddings, db.snapshots, db.assets], async () => {
      // Purge everything hanging off this workspace's files too —
      // otherwise embeddings/snapshots/assets accumulate as orphan rows
      // and quietly eat the IndexedDB quota.
      const fileIds = await db.files.where('workspaceId').equals(workspaceId).primaryKeys();
      await db.embeddings.where('workspaceId').equals(workspaceId).delete();
      await db.snapshots.where('fileId').anyOf(fileIds).delete();
      await db.assets.where('workspaceId').equals(workspaceId).delete();
      await db.files.where('workspaceId').equals(workspaceId).delete();
      await db.workspaces.delete(workspaceId);
    });

    const { workspaces, activeWorkspaceId } = get();
    const remaining = workspaces.filter((ws) => ws.id !== workspaceId);

    if (workspaceId === activeWorkspaceId) {
      if (remaining.length > 0) {
        // Switch to next workspace
        set({ workspaces: remaining });
        await get().switchWorkspace(remaining[0].id);
      } else {
        // No workspaces left — show upload screen
        set({
          workspaces: [],
          activeWorkspaceId: null,
          files: [],
          activeFileId: null,
          activeFileContent: null,
        });
      }
    } else {
      set({ workspaces: remaining });
    }
  },

  // ---------- Rename Workspace ----------
  renameWorkspace: async (workspaceId, title) => {
    await db.workspaces.update(workspaceId, { title, updatedAt: new Date() });

    set((state) => ({
      workspaces: state.workspaces.map((ws) =>
        ws.id === workspaceId ? { ...ws, title, updatedAt: new Date() } : ws
      ),
    }));
  },

  // ---------- Rename File ----------
  renameFile: async (fileId, newName) => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    // Preserve an extension: "notes" → "notes.md", "notes.markdown" kept.
    const filename = /\.(md|markdown)$/i.test(trimmed) ? trimmed : `${trimmed}.md`;
    const displayName = filename.replace(/\.(md|markdown)$/i, '');
    await db.files.update(fileId, { filename, displayName });
    set((state) => ({
      files: state.files.map((f) =>
        f.id === fileId ? { ...f, filename, displayName } : f
      ),
    }));
  },

  // ---------- Set Active File (lazy load content) ----------
  // Cross-fade the document swap via the View Transitions API. We load
  // the content first (IndexedDB read is usually <10ms) and only then
  // commit the state mutation, so the snapshot the browser captures has
  // the old document fully painted and the new one ready to fade in.
  // Falls back to a normal set() on browsers without the API.
  //
  // N16 — per-file scroll memory. Before swapping, snapshot the
  // outgoing file's scrollTop to localStorage; after the commit, fade
  // in at the incoming file's saved position (or 0).
  setActiveFile: async (fileId) => {
    const outgoingId = get().activeFileId;
    const main = typeof document !== 'undefined'
      ? document.querySelector<HTMLElement>('.viewer-main')
      : null;
    if (outgoingId && main && typeof localStorage !== 'undefined') {
      try { localStorage.setItem(`mv-scroll-${outgoingId}`, String(main.scrollTop)); } catch { /* ignore */ }
    }
    const token = ++fileSwitchToken;
    const dbFile = await db.files.get(fileId);
    if (token !== fileSwitchToken) return; // superseded by a newer file switch
    const commit = () => {
      set({
        activeFileId: fileId,
        activeFileContent: dbFile?.content ?? null,
        isContentLoading: false,
      });
    };
    const d = typeof document !== 'undefined' ? document as Document & {
      startViewTransition?: (cb: () => void) => unknown;
    } : null;
    // A view transition snapshots + crossfades the whole page. With a painting
    // atmosphere active that means rasterizing the heavy 9-layer paper +
    // painting + ambient twice per file switch — the source of the file-change
    // lag. Skip it when an atmosphere is on (and under reduced-motion); the
    // content just swaps instantly. Keep the crossfade for the light plain view.
    const atmo = d?.documentElement.getAttribute('data-atmosphere');
    const heavy = atmo && atmo !== 'none';
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (d && typeof d.startViewTransition === 'function' && !heavy && !reduceMotion) {
      d.startViewTransition(commit);
    } else {
      commit();
    }
    // Restore scroll on next frames once new content has rendered.
    if (typeof window !== 'undefined') {
      let saved: number = 0;
      try { saved = parseInt(localStorage.getItem(`mv-scroll-${fileId}`) || '0', 10) || 0; } catch { /* ignore */ }
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const m = document.querySelector<HTMLElement>('.viewer-main');
        if (m) m.scrollTop = saved;
      }));
    }
  },

  // ---------- Add Files to Active Workspace ----------
  addFiles: async (inputFiles) => {
    const { activeWorkspaceId, files, workspaces } = get();
    // No active workspace yet (e.g. a drop that lands before workspace
    // creation commits) — don't silently lose the user's files; seed a
    // fresh workspace with them instead.
    if (!activeWorkspaceId) {
      if (inputFiles.length > 0) await get().createWorkspace(deriveDisplayName(inputFiles[0].filename), inputFiles);
      return;
    }

    const existingMax = Math.max(...files.map((f) => f.order), -1);
    const newFiles: (FileMeta & { content: string })[] = inputFiles.map((f, i) => ({
      id: generateId(),
      filename: f.filename,
      displayName: deriveDisplayName(f.filename),
      content: f.content,
      order: existingMax + 1 + i,
      size: new Blob([f.content]).size,
    }));

    const dbFiles: DBFile[] = newFiles.map((f) => ({
      ...f,
      workspaceId: activeWorkspaceId,
    }));

    const addedSize = newFiles.reduce((sum, f) => sum + f.size, 0);

    await db.files.bulkAdd(dbFiles);
    await db.workspaces.update(activeWorkspaceId, {
      updatedAt: new Date(),
      fileCount: files.length + newFiles.length,
      totalSize: (workspaces.find((ws) => ws.id === activeWorkspaceId)?.totalSize || 0) + addedSize,
    });

    const newFileMetas: FileMeta[] = newFiles.map(({ content: _, ...meta }) => meta);

    set((state) => ({
      files: [...state.files, ...newFileMetas],
      workspaces: state.workspaces.map((ws) =>
        ws.id === activeWorkspaceId
          ? {
              ...ws,
              fileCount: ws.fileCount + newFiles.length,
              totalSize: ws.totalSize + addedSize,
              updatedAt: new Date(),
            }
          : ws
      ),
    }));

    // Files added to an existing workspace only ever come from a deliberate
    // user action, so no system-workspace guard is needed here.
    void ensurePersistentStorage();
  },

  // ---------- Remove File ----------
  removeFile: async (fileId) => {
    const { activeWorkspaceId, files, activeFileId, workspaces } = get();
    if (!activeWorkspaceId) return;

    const removedFile = files.find((f) => f.id === fileId);
    const removedDbFile = await db.files.get(fileId);
    await db.files.delete(fileId);
    // Drop embeddings + snapshots so we don't leave orphan rows.
    await db.embeddings.where('fileId').equals(fileId).delete();
    await db.snapshots.where('fileId').equals(fileId).delete();
    // GC pasted-image assets referenced only by the removed file —
    // without this they sit in IndexedDB eating quota until the whole
    // workspace is deleted.
    const assetRe = /\(asset:([\w-]+)\)/g;
    const removedAssetIds = [...(removedDbFile?.content.matchAll(assetRe) ?? [])].map((m) => m[1]);
    if (removedAssetIds.length > 0) {
      const siblings = await db.files.where('workspaceId').equals(activeWorkspaceId).toArray();
      const stillReferenced = new Set<string>();
      for (const f of siblings) {
        for (const m of f.content.matchAll(assetRe)) stillReferenced.add(m[1]);
      }
      const orphans = removedAssetIds.filter((id) => !stillReferenced.has(id));
      if (orphans.length > 0) await db.assets.bulkDelete(orphans);
    }

    const newFiles = files.filter((f) => f.id !== fileId);
    const removedSize = removedFile?.size || 0;

    await db.workspaces.update(activeWorkspaceId, {
      updatedAt: new Date(),
      fileCount: newFiles.length,
      totalSize: Math.max(0, (workspaces.find((ws) => ws.id === activeWorkspaceId)?.totalSize || 0) - removedSize),
    });

    // If removed file was active, switch to next
    if (activeFileId === fileId) {
      const nextFile = newFiles.length > 0 ? newFiles[0] : null;
      let nextContent: string | null = null;
      if (nextFile) {
        const dbFile = await db.files.get(nextFile.id);
        nextContent = dbFile?.content ?? null;
      }
      set({
        files: newFiles,
        activeFileId: nextFile?.id || null,
        activeFileContent: nextContent,
        workspaces: get().workspaces.map((ws) =>
          ws.id === activeWorkspaceId
            ? { ...ws, fileCount: newFiles.length, totalSize: ws.totalSize - removedSize, updatedAt: new Date() }
            : ws
        ),
      });
    } else {
      set({
        files: newFiles,
        workspaces: get().workspaces.map((ws) =>
          ws.id === activeWorkspaceId
            ? { ...ws, fileCount: newFiles.length, totalSize: ws.totalSize - removedSize, updatedAt: new Date() }
            : ws
        ),
      });
    }
  },

  // ---------- Update active file content in place ----------
  updateActiveFileContent: async (content) => {
    const { activeFileId } = get();
    if (!activeFileId) return;
    set({ activeFileContent: content });
    try {
      await db.files.update(activeFileId, { content });
    } catch (err) {
      console.warn('[workspace] failed to persist inline edit', err);
    }
  },

  // ---------- Reorder Files ----------
  reorderFiles: (fromIndex, toIndex) => {
    const { files } = get();
    if (fromIndex < 0 || fromIndex >= files.length) return;
    if (toIndex < 0 || toIndex >= files.length) return;
    if (fromIndex === toIndex) return;

    const reordered = [...files];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    // Renumber `order` to match the new positions and persist it — files are
    // loaded `.sortBy('order')`, so without this the reorder reverts on reload.
    const newFiles = reordered.map((f, i) => ({ ...f, order: i }));
    set({ files: newFiles });
    db.transaction('rw', db.files, async () => {
      for (let i = 0; i < newFiles.length; i++) {
        await db.files.update(newFiles[i].id, { order: i });
      }
    }).catch((err) => console.warn('[workspace] failed to persist file order', err));
  },

  // ---------- Move File To Another Workspace ----------
  moveFileToWorkspace: async (fileId, targetWorkspaceId) => {
    const { workspaces, activeWorkspaceId, files: currentFiles, activeFileId } = get();
    const dbFile = await db.files.get(fileId);
    if (!dbFile) return false;
    if (dbFile.workspaceId === targetWorkspaceId) return false;

    const sourceWsId = dbFile.workspaceId;
    const targetWs = workspaces.find((w) => w.id === targetWorkspaceId);
    if (!targetWs) return false;

    // Drop the file at the end of the target's file list.
    const targetFiles = await db.files.where('workspaceId').equals(targetWorkspaceId).toArray();
    const newOrder = targetFiles.length > 0 ? Math.max(...targetFiles.map((f) => f.order)) + 1 : 0;

    await db.files.update(fileId, {
      workspaceId: targetWorkspaceId,
      order: newOrder,
    });
    // Embeddings are scoped by workspaceId — move them along or semantic
    // search keeps surfacing this file in the OLD workspace and never
    // finds it in the new one.
    await db.embeddings.where('fileId').equals(fileId).modify({ workspaceId: targetWorkspaceId });
    const now = new Date();
    await db.workspaces.update(sourceWsId, {
      updatedAt: now,
      fileCount: Math.max(0, (workspaces.find((w) => w.id === sourceWsId)?.fileCount ?? 1) - 1),
      totalSize: Math.max(0, (workspaces.find((w) => w.id === sourceWsId)?.totalSize ?? dbFile.size) - dbFile.size),
    });
    await db.workspaces.update(targetWorkspaceId, {
      updatedAt: now,
      fileCount: (targetWs.fileCount || 0) + 1,
      totalSize: (targetWs.totalSize || 0) + dbFile.size,
    });

    // Local state update — only the active workspace's `files` list lives
    // in store; others are loaded on demand by switchWorkspace.
    if (sourceWsId === activeWorkspaceId) {
      const remaining = currentFiles.filter((f) => f.id !== fileId);
      const wasActive = activeFileId === fileId;
      let nextActiveFile: string | null = activeFileId;
      let nextContent: string | null = get().activeFileContent;
      if (wasActive) {
        const nextFile = remaining[0] ?? null;
        nextActiveFile = nextFile?.id ?? null;
        if (nextFile) {
          nextContent = (await db.files.get(nextFile.id))?.content ?? null;
        } else {
          nextContent = null;
        }
      }
      set({
        files: remaining,
        activeFileId: nextActiveFile,
        activeFileContent: nextContent,
        workspaces: workspaces.map((w) =>
          w.id === sourceWsId
            ? { ...w, fileCount: Math.max(0, w.fileCount - 1), totalSize: Math.max(0, w.totalSize - dbFile.size), updatedAt: now }
            : w.id === targetWorkspaceId
              ? { ...w, fileCount: (w.fileCount || 0) + 1, totalSize: (w.totalSize || 0) + dbFile.size, updatedAt: now }
              : w
        ),
      });
    } else {
      set({
        workspaces: workspaces.map((w) =>
          w.id === sourceWsId
            ? { ...w, fileCount: Math.max(0, w.fileCount - 1), totalSize: Math.max(0, w.totalSize - dbFile.size), updatedAt: now }
            : w.id === targetWorkspaceId
              ? { ...w, fileCount: (w.fileCount || 0) + 1, totalSize: (w.totalSize || 0) + dbFile.size, updatedAt: now }
              : w
        ),
      });
    }

    return true;
  },

  // ---------- Promote File To New Workspace ----------
  promoteFileToNewWorkspace: async (fileId, newTitle) => {
    const dbFile = await db.files.get(fileId);
    if (!dbFile) return;

    const title = newTitle || deriveDisplayName(dbFile.filename) || 'new workspace';
    // Re-use createWorkspace + then removeFile from the original to avoid
    // duplicating ID-generation + storage logic.
    await get().createWorkspace(title, [{ filename: dbFile.filename, content: dbFile.content }]);
    // The original file remains in its workspace; remove it now that the
    // copy lives in the new one. We use the same fileId path: temporarily
    // switch back to the original workspace to use removeFile cleanly.
    const sourceWsId = dbFile.workspaceId;
    const newActiveWsId = get().activeWorkspaceId;
    if (sourceWsId !== newActiveWsId) {
      // Delete directly from DB without switching context — removeFile's
      // local-state updates only apply to the active workspace and we're
      // already on the new one. The source workspace's file count gets
      // recomputed lazily on next switchWorkspace.
      await db.files.delete(fileId);
      // Same orphan cleanup removeFile does — stale embedding rows keep
      // the source workspaceId and make semantic search surface a
      // deleted file.
      await db.embeddings.where('fileId').equals(fileId).delete();
      await db.snapshots.where('fileId').equals(fileId).delete();
      const sourceWs = get().workspaces.find((w) => w.id === sourceWsId);
      if (sourceWs) {
        await db.workspaces.update(sourceWsId, {
          updatedAt: new Date(),
          fileCount: Math.max(0, sourceWs.fileCount - 1),
          totalSize: Math.max(0, sourceWs.totalSize - dbFile.size),
        });
        set({
          workspaces: get().workspaces.map((w) =>
            w.id === sourceWsId
              ? { ...w, fileCount: Math.max(0, w.fileCount - 1), totalSize: Math.max(0, w.totalSize - dbFile.size), updatedAt: new Date() }
              : w
          ),
        });
      }
    }
  },

  // ---------- Reorder Workspaces ----------
  reorderWorkspaces: (fromIndex, toIndex) => {
    const { workspaces } = get();
    if (fromIndex < 0 || fromIndex >= workspaces.length) return;
    if (toIndex < 0 || toIndex >= workspaces.length) return;
    if (fromIndex === toIndex) return;

    // Persist order by updating timestamps so most-recently-ordered stays
    // first. Keep the in-memory metas in lockstep with what we write so
    // store and DB can't disagree until reload.
    const now = Date.now();
    const reordered = [...workspaces];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const updated = reordered.map((ws, i) => ({ ...ws, updatedAt: new Date(now - i) }));
    set({ workspaces: updated });

    db.transaction('rw', db.workspaces, async () => {
      for (let i = 0; i < updated.length; i++) {
        await db.workspaces.update(updated[i].id, {
          updatedAt: updated[i].updatedAt,
        });
      }
    }).catch((err) => {
      console.warn('[workspaces] failed to persist reorder', err);
    });
  },
}));
